import api from './api';

export const adminAPI = {
  // --- User Management functions (unchanged) ---

  // In src/services/adminAPI.js - Replace the existing getUsers function

  // Modified getUsers function that properly includes organizationId
// Fixed getUsers function to include organizationId in the request
getUsers: async () => {
  console.log('Starting getUsers API call');
  try {
    // Get the organization ID from localStorage if available
    const organizationId = localStorage.getItem('organizationId');
    
    // Log the request configuration with organization info
    const config = {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
      },
      params: {
        organization_id: organizationId
      }
    };
    console.log('Request config for getUsers:', config);

    const response = await api.get('/admin/users', config);
    
    // Log the complete raw response
    console.log('Complete API Response from getUsers:', {
      status: response.status,
      headers: response.headers,
      data: response.data,
      config: response.config
    });

    // Validate the response structure
    if (!response.data) {
      console.error('No data in response from getUsers');
      return { data: { success: true, users: [] } };
    }

    // Log the users array specifically
    const users = response.data.users || [];
    console.log('Users from API:', {
      count: users.length,
      userIds: users.map(u => u.id),
      emails: users.map(u => u.email)
    });

    return {
      data: {
        success: true,
        users: users
      }
    };
  } catch (error) {
    console.error('Error in getUsers:', {
      message: error.message,
      status: error.response?.status,
      data: error.response?.data,
      config: error.config
    });
    throw error;
  }
},
  
  createUser: async (userData) => {
    try {
      const response = await api.post('/admin/users', userData);
      return response;
    } catch (error) {
      if (error.response?.data?.message === 'User is not authorized for admin access') {
        throw new Error('You do not have admin permissions to create users');
      }
      console.error('Admin API createUser error:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      throw error;
    }
  },
  
  updateUser: async (userId, userData) => {
    try {
      const { has_seat, ...basicData } = userData;
      const responseBasic = await api.put(`/admin/users/${userId}`, basicData);
      const responseSeat = await api.put(`/admin/users/${userId}/seat`, { has_seat });
      return {
        basic: responseBasic.data,
        seat: responseSeat.data,
      };
    } catch (error) {
      if (error.response?.data?.message === 'User is not authorized for admin access') {
        throw new Error('You do not have admin permissions to update users');
      }
      console.error('Admin API updateUser error:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      throw error;
    }
  },
  
  updateUserSeat: async (userId, has_seat) => {
    const response = await api.put(`/admin/users/${userId}/seat`, { has_seat });
    return response;
  },

  deleteUser: async (userId) => {
    try {
      const response = await api.delete(`/admin/users/${userId}`);
      return response;
    } catch (error) {
      if (error.response?.data?.message === 'User is not authorized for admin access') {
        throw new Error('You do not have admin permissions to delete users');
      }
      console.error('Admin API deleteUser error:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      throw error;
    }
  },

  // --- Plan Management ---
  getPlans: async () => {
    try {
      // This endpoint must exist on your backend.
      const response = await api.get('/subscription/plans');
      return response;
    } catch (error) {
      if (error.response?.data?.message === 'User is not authorized for admin access') {
        throw new Error('You do not have admin permissions to view plans');
      }
      console.error('Admin API getPlans error:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      throw error;
    }
  },
  
  getBillingHistory: async (organizationId) => {
    try {
      const response = await api.get(`/subscription/organizations/${organizationId}/seats/history`);
      return response;
    } catch (error) {
      if (error.response?.data?.message === 'User is not authorized for admin access') {
        throw new Error('You do not have admin permissions to view billing history');
      }
      console.error('Admin API getBillingHistory error:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      throw error;
    }
  },

  getCurrentAdmin: async () => {
    const response = await api.get('/admin/current');
    return response;
  },

  // --- Subscription Management ---
  getSubscription: async () => {
    try {
      // Updated to use the route defined in your subscription routes.
      const response = await api.get('/subscription/admin/subscription');
      console.log('Subscription response:', response);
      return response;
    } catch (error) {
      if (error.response?.status === 404) {
        return {
          data: {
            subscription: {
              seats_allocated: 0,
              seats_used: 0,
              status: 'inactive'
            }
          }
        };
      }
      console.error('Admin API getSubscription error:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      throw error;
    }
  },

  createSubscription: async (organizationId, subscriptionData) => {
    try {
      const response = await api.post(`/subscription/organizations/${organizationId}/subscribe`, subscriptionData);
      return response;
    } catch (error) {
      if (error.response?.data?.message === 'User is not authorized for admin access') {
        throw new Error('You do not have admin permissions to create subscriptions');
      }
      console.error('Admin API createSubscription error:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      throw error;
    }
  },

  updateSubscription: async (organizationId, subscriptionId, data) => {
    try {
      // Updated to use the PATCH route for seats update.
      const response = await api.patch(`/organizations/${organizationId}/seats`, data);
      return response;
    } catch (error) {
      if (error.response?.data?.message === 'User is not authorized for admin access') {
        throw new Error('You do not have admin permissions to update subscriptions');
      }
      console.error('Admin API updateSubscription error:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      throw error;
    }
  },

  cancelSubscription: async (organizationId, subscriptionId, cancelImmediately) => {
    try {
      const response = await api.post(`/admin/organizations/${organizationId}/subscriptions/${subscriptionId}/cancel`, { cancelImmediately });
      return response;
    } catch (error) {
      if (error.response?.data?.message === 'User is not authorized for admin access') {
        throw new Error('You do not have admin permissions to cancel subscriptions');
      }
      console.error('Admin API cancelSubscription error:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      throw error;
    }
  },

  // --- Seat Management ---
  assignSeat: async (organizationId, userId) => {
    try {
      const response = await api.post(`/admin/organizations/${organizationId}/seats/assign`, { userId });
      return response;
    } catch (error) {
      if (error.response?.data?.message === 'User is not authorized for admin access') {
        throw new Error('You do not have admin permissions to assign seats');
      }
      console.error('Admin API assignSeat error:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      throw error;
    }
  },

  unassignSeat: async (organizationId, userId) => {
    try {
      const response = await api.post(`/admin/organizations/${organizationId}/seats/unassign`, { userId });
      return response;
    } catch (error) {
      if (error.response?.data?.message === 'User is not authorized for admin access') {
        throw new Error('You do not have admin permissions to unassign seats');
      }
      console.error('Admin API unassignSeat error:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      throw error;
    }
  },

  getUsageStats: async (organizationId, startDate, endDate) => {
    try {
      const response = await api.get(`/admin/organizations/${organizationId}/usage`, {
        params: { startDate, endDate }
      });
      return response;
    } catch (error) {
      if (error.response?.data?.message === 'User is not authorized for admin access') {
        throw new Error('You do not have admin permissions to view usage stats');
      }
      console.error('Admin API getUsageStats error:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      throw error;
    }
  },

  getSeatUsage: async () => {
    try {
      const response = await api.get('/admin/organization/seat-usage');
      console.log('Seat usage response:', response);
      return response;
    } catch (error) {
      console.error('Admin API getSeatUsage error:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      throw error;
    }
  },

  validateSeatAvailability: async (organizationId) => {
    try {
      const response = await api.get(`/subscription/organizations/${organizationId}/seats/validate`);
      return response;
    } catch (error) {
      if (error.response?.data?.message === 'User is not authorized for admin access') {
        throw new Error('You do not have admin permissions to validate seats');
      }
      console.error('Admin API validateSeatAvailability error:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      throw error;
    }
  },

  getOrganizationDetails: async () => {
    try {
      const response = await api.get('/admin/organization/details');
      console.log('Organization details response:', response);
      return response;
    } catch (error) {
      console.error('Admin API getOrganizationDetails error:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      throw error;
    }
  },

  createUserWithSeat: async (userData) => {
    try {
      const response = await api.post('/admin/users/with-seat', userData);
      return response;
    } catch (error) {
      if (error.response?.status === 403) {
        throw new Error('You do not have admin permissions to create users');
      }
      if (error.response?.status === 400) {
        throw new Error(error.response.data.message || 'Invalid user data');
      }
      if (error.response?.status === 409) {
        throw new Error('No available seats. Please upgrade your subscription.');
      }
      console.error('Admin API createUserWithSeat error:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      throw error;
    }
  },

  updateSubscriptionSeats: async (organizationId, newSeatCount) => {
    try {
      const response = await api.patch(
        `/subscription/organizations/${organizationId}/seats`,
        { seats_allocated: newSeatCount }
      );
      return response;
    } catch (error) {
      if (error.response?.status === 403) {
        throw new Error('You do not have admin permissions to update seats');
      }
      if (error.response?.status === 400) {
        throw new Error(error.response.data.message || 'Invalid seat count');
      }
      console.error('Admin API updateSubscriptionSeats error:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      throw error;
    }
  },

  getSeatAllocationHistory: async (organizationId) => {
    try {
      const response = await api.get(`/subscription/organizations/${organizationId}/seats/history`);
      return response;
    } catch (error) {
      if (error.response?.status === 403) {
        throw new Error('You do not have admin permissions to view seat history');
      }
      console.error('Admin API getSeatAllocationHistory error:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      throw error;
    }
  },

  transferSeat: async (organizationId, fromUserId, toUserId) => {
    try {
      const response = await api.post(`/subscription/organizations/${organizationId}/seats/transfer`, {
        from_user_id: fromUserId,
        to_user_id: toUserId
      });
      return response;
    } catch (error) {
      if (error.response?.status === 403) {
        throw new Error('You do not have admin permissions to transfer seats');
      }
      if (error.response?.status === 400) {
        throw new Error(error.response.data.message || 'Invalid seat transfer request');
      }
      console.error('Admin API transferSeat error:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      throw error;
    }
  },

// Add these new methods to your adminAPI.js file

// Set a user as Google Workspace admin
// In adminAPI.js
setGoogleWorkspaceAdmin: async (userId) => {
  try {
    const response = await api.post('/admin/organizations/set-google-workspace-admin', { userId });
    return response;
  } catch (error) {
    console.error('Admin API setGoogleWorkspaceAdmin error:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
    });
    throw error;
  }
},

// Sync Google Workspace users group
syncGoogleWorkspaceUsersGroup: async (organizationId, domain) => {
  try {
    const response = await api.post(`/admin/organizations/${organizationId}/sync-google-workspace-users-group`, { domain });
    return response;
  } catch (error) {
    console.error('Admin API syncGoogleWorkspaceUsersGroup error:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
    });
    throw error;
  }
}

};